/**
 * atomberg-razorpay-id.js
 *
 * Custom (no-iframe) RazorpayId login — headless SDK mode. We build every
 * screen ourselves (mobile -> OTP + consent tick -> name/email where needed),
 * matching the backend OIDC interaction page, and call the SDK's
 * initAuth/verifyAuth/oneClickLogin directly. See razorpay-id-login.controller.ts
 * in atomberg-shopify-backend for the headless/* endpoints.
 *
 * Every page load a guest is in exactly one of these flows:
 *   manual  — clicked Login: mobile -> OTP -> consent -> resolve (consent: true).
 *             Always asks for the number, even when Razorpay recognizes the
 *             browser, so a shopper who just logged out can switch accounts.
 *   auto    — Razorpay recognizes the browser AND the atomberg_rzp_auto_login
 *             cookie is set: silent oneClickLogin -> resolve (no consent sent;
 *             it was given on the login that set the cookie) -> logged in.
 *             If that login can't complete (the customer record is gone, or
 *             has no email), the backend records them as a silent capture
 *             instead and answers 'captured' — handled exactly like capture,
 *             and the cookie is cleared so later loads use capture's
 *             per-identity dedupe instead of retrying every page.
 *   capture — Razorpay recognizes the browser, no cookie, once per tab
 *             session: silent oneClickLogin -> capture. Finds or creates the
 *             Shopify customer (consent 'missing' if new) and identifies them
 *             in MoEngage. Nobody is logged in.
 *
 * The auto-login cookie is off by default, set by any logged-in page load,
 * and cleared when a guest page load arrives from the customer-accounts
 * domain or our backend — i.e. right after a logout (Shopify's logout ends on
 * our backend's "You're signed out" page) or an abandoned login. Without it,
 * Razorpay's session outliving a Shopify logout would log the shopper straight
 * back in on the next page.
 *
 * Manual mode falls back to the classic OTP login (fallbackLoginUrl) if
 * Razorpay's OTP delivery fails before the OTP step is ever reached — see
 * the login.failure handler.
 *
 * Manual mode also reports its funnel to MoEngage (razorpay_otp_sent,
 * razorpay_login_failed, razorpay_details_required — see trackLogin()) so a
 * drop-off is diagnosable from MoEngage alone. auto/capture stay silent, by
 * the same design as their own failures.
 */
(function () {
  'use strict';

  var w = /** @type {any} */ (window);
  if (w._atombergRazorpayIdInit) return;
  w._atombergRazorpayIdInit = true;

  var SDK_SRC = 'https://checkout.razorpay.com/v1/razorpay-id.js';
  var AUTO_LOGIN_COOKIE = 'atomberg_rzp_auto_login';
  var AUTO_LOGIN_COOKIE_MAX_AGE = 31536000;
  // localStorage (survives tab close), not sessionStorage: capture should
  // run once per recognized identity, not once per tab. See shouldCapture().
  var CAPTURE_STATE_KEY = 'atomberg_rzp_capture_state';
  var OTP_LENGTH = 6;
  var RESEND_SECONDS = 30;

  /** @type {Record<string, {title: string, subtitle: string}>} */
  var STEP_COPY = {
    phone: { title: 'Sign in', subtitle: 'Log in securely with your mobile number.' },
    otp: { title: 'Sign in', subtitle: 'Log in securely with your mobile number.' },
    details: {
      title: 'Complete your profile',
      subtitle: 'Just a few more details to finish signing in.',
    },
    loading: { title: 'Sign in', subtitle: '' },
  };
  var EMAIL_ONLY_COPY = {
    title: 'Add your email',
    subtitle: 'We need an email address to finish signing you in.',
  };

  var config = /** @type {HTMLElement|null} */ (document.getElementById('atomberg-razorpay-id'));
  var popupEl = /** @type {HTMLElement|null} */ (
    document.getElementById('atomberg-razorpay-id-popup')
  );
  if (!config || !popupEl) return;

  // Reassigned to a non-nullable-typed binding — the helpers below are plain
  // function declarations, not closures over this exact guard, so JSDoc/TS
  // can't otherwise carry the !config/!popupEl narrowing into them.
  /** @type {HTMLElement} */
  var popup = popupEl;

  var key = config.dataset.key || '';
  var resolveEndpoint = config.dataset.resolveEndpoint || '';
  var profileEndpoint = config.dataset.profileEndpoint || '';
  var detailsEndpoint = config.dataset.detailsEndpoint || '';
  var captureEndpoint = config.dataset.captureEndpoint || '';
  var triggerSelector = config.dataset.triggerSelector || '';
  var fallbackLoginUrl = config.dataset.fallbackLoginUrl || '';
  var isLoggedIn = config.dataset.loggedIn === 'true';

  if (
    !key ||
    !resolveEndpoint ||
    !profileEndpoint ||
    !detailsEndpoint ||
    !captureEndpoint ||
    !triggerSelector
  ) {
    return;
  }

  /** @type {any} */
  var sso = null;
  var ready = false;
  // The one silent oneClickLogin in flight, if any. Its login.success /
  // login.failure is always the next one the SDK emits, since a manual login
  // needs the shopper to type an OTP first — and sendOtp/verifyOtp clear this
  // anyway, so a silent call that never reports back can't hijack them.
  /** @type {'auto'|'capture'|null} */
  var silent = null;
  var manualOpen = false;
  /** @type {string|null} */
  var pendingId = null;
  // What the details step is collecting: create=true is a brand-new customer
  // (profile endpoint, every field); false is an existing one (details
  // endpoint, only the fields its record lacks).
  var details = { create: true, name: true, email: true };
  var contactDigits = '';
  // The suffix the init handler decided to capture for — read back by
  // captureCustomer() once Razorpay's login.success confirms it.
  var pendingCaptureSuffix = '';
  /** @type {number|undefined} */
  var resendInterval;

  // ── Storage helpers ──────────────────────────────────────────────────────

  /** @param {string} name */
  function readCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  /**
   * @param {string} name
   * @param {string} value
   * @param {number} maxAgeSeconds
   */
  function writeCookie(name, value, maxAgeSeconds) {
    document.cookie =
      name + '=' + value + '; path=/; max-age=' + maxAgeSeconds + '; SameSite=Lax; Secure';
  }

  /** @returns {{suffix: string, phoneHash: string, status: string}|null} */
  function readCaptureState() {
    try {
      var raw = localStorage.getItem(CAPTURE_STATE_KEY);
      return raw ? JSON.parse(raw) : { suffix: '', phoneHash: '', status: '' };
    } catch (e) {
      return null; // Storage broken/unavailable (e.g. some private modes) — caller treats this as "don't capture".
    }
  }

  /**
   * SHA-256 hex digest — used so the full phone number the backend hands back
   * never sits in localStorage as plain digits. Not usable for the pre-call
   * decision (shouldCapture only ever has the suffix to go on — the SDK
   * doesn't expose the full number until after this exchange), only to keep
   * what's stored at rest from being readable PII.
   * @param {string} text
   * @returns {Promise<string>}
   */
  function sha256Hex(text) {
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)).then(function (buf) {
      return Array.prototype.map
        .call(new Uint8Array(buf), function (b) {
          return b.toString(16).padStart(2, '0');
        })
        .join('');
    });
  }

  /**
   * @param {string} suffix
   * @param {string} mobile - Full E.164 number from the backend response, if any.
   * @param {string} status
   */
  function writeCaptureState(suffix, mobile, status) {
    /** @param {string} phoneHash */
    var finish = function (phoneHash) {
      try {
        localStorage.setItem(
          CAPTURE_STATE_KEY,
          JSON.stringify({ suffix: suffix, phoneHash: phoneHash, status: status })
        );
      } catch (e) {
        // Nothing to do — readCaptureState() already reports null without storage.
      }
    };
    if (mobile && w.crypto && w.crypto.subtle) {
      sha256Hex(mobile)
        .then(finish)
        .catch(function () {
          finish('');
        });
    } else {
      finish('');
    }
  }

  /**
   * Capture should run once per recognized identity (Razorpay's contact_suffix,
   * the only client-visible identifier before the backend resolves the full
   * phone), not once per tab: a returning guest opening a new tab shouldn't
   * burn a fresh Razorpay one-time code and hit the backend for a customer we
   * already confirmed exists. Runs again when a different suffix shows up
   * (e.g. a shared device) or the last attempt didn't succeed (network/backend
   * error) — those are the two cases worth retrying for.
   * @param {string} suffix
   */
  function shouldCapture(suffix) {
    var state = readCaptureState();
    if (!state) return false;
    return state.suffix !== suffix || state.status !== 'success';
  }

  /** @param {string} url */
  function originOf(url) {
    try {
      return new URL(url, window.location.href).origin;
    } catch (e) {
      return '';
    }
  }

  // ── Auto-login gate ──────────────────────────────────────────────────────

  // Real RP-initiated logout redirects back through our own backend's
  // "You're signed out" page (see rpInitiatedLogout/postLogoutSuccessSource
  // in oidc.provider.ts) — that's the only reliable "just logged out"
  // signal. routes.account_url is deliberately NOT used here: under new
  // Customer Accounts its origin is just https://shopify.com, which is not
  // specific to this store — Magic Checkout, Shop Pay, and anything else
  // Shopify-hosted would look identical to a logout and wipe the cookie on
  // an ordinary visit. The storefront's own origin is excluded so plain
  // in-site navigation never counts.
  var authOrigins = [originOf(resolveEndpoint)].filter(function (origin) {
    return origin && origin !== window.location.origin;
  });
  var referrerOrigin = document.referrer ? originOf(document.referrer) : '';
  var justLoggedOut = false;
  // A reload or back/forward keeps the original document.referrer, so without
  // this every refresh of the page a logout landed on would count as a fresh
  // logout and clear the cookie again — only a real navigation counts.
  var navEntry = /** @type {PerformanceNavigationTiming|undefined} */ (
    performance.getEntriesByType('navigation')[0]
  );
  var freshArrival = !navEntry || navEntry.type === 'navigate';

  if (isLoggedIn) {
    writeCookie(AUTO_LOGIN_COOKIE, '1', AUTO_LOGIN_COOKIE_MAX_AGE);
  } else if (freshArrival && referrerOrigin && authOrigins.indexOf(referrerOrigin) !== -1) {
    writeCookie(AUTO_LOGIN_COOKIE, '', 0);
    // Just logged out — their customer record exists; capturing now would
    // only re-identify the device in MoEngage. One-shot for this page load
    // only — not persisted, so a later visit still captures normally if
    // something about this browser's recognized identity actually changes.
    justLoggedOut = true;
  }
  var autoLoginAllowed = !isLoggedIn && readCookie(AUTO_LOGIN_COOKIE) === '1';

  // ── DOM helpers ──────────────────────────────────────────────────────────

  /** @param {string} step */
  function showStep(step) {
    var steps = popup.querySelectorAll('[data-step]');
    for (var i = 0; i < steps.length; i++) {
      /** @type {HTMLElement} */ (steps[i]).hidden = steps[i].getAttribute('data-step') !== step;
    }
    var copy = STEP_COPY[step];
    var title = popup.querySelector('[data-title]');
    var subtitle = /** @type {HTMLElement|null} */ (popup.querySelector('[data-subtitle]'));
    if (copy && title) title.textContent = copy.title;
    if (copy && subtitle) {
      subtitle.textContent = copy.subtitle;
      subtitle.hidden = !copy.subtitle;
    }
  }

  /**
   * @param {string} name
   * @param {string} message
   */
  function showError(name, message) {
    var el = /** @type {HTMLElement|null} */ (popup.querySelector('[data-error="' + name + '"]'));
    if (el) el.textContent = message;
  }

  /** @param {string} fieldName @returns {HTMLInputElement|null} */
  function field(fieldName) {
    return /** @type {HTMLInputElement|null} */ (
      popup.querySelector('[data-field="' + fieldName + '"]')
    );
  }

  /** @param {string} action @returns {HTMLButtonElement|null} */
  function actionButton(action) {
    return /** @type {HTMLButtonElement|null} */ (
      popup.querySelector('[data-action="' + action + '"]')
    );
  }

  /**
   * @param {HTMLButtonElement|null} btn
   * @param {boolean} on
   */
  function setLoading(btn, on) {
    if (!btn) return;
    btn.classList.toggle('is-loading', on);
    btn.disabled = on;
  }

  /** @param {string} value */
  function digits(value) {
    return (value || '').replace(/\D/g, '');
  }

  /** @param {string} value */
  function validEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function consentGiven() {
    var box = field('consent');
    return !!(box && box.checked);
  }

  /**
   * @param {boolean} create
   * @param {boolean} needName
   * @param {boolean} needEmail
   * @param {{first_name?: string, last_name?: string, email?: string}|undefined} prefill
   */
  function showDetails(create, needName, needEmail, prefill) {
    details = { create: create, name: needName, email: needEmail };
    var nameGroup = /** @type {HTMLElement|null} */ (popup.querySelector('[data-group="name"]'));
    var emailGroup = /** @type {HTMLElement|null} */ (popup.querySelector('[data-group="email"]'));
    if (nameGroup) nameGroup.hidden = !needName;
    if (emailGroup) emailGroup.hidden = !needEmail;
    // Razorpay's values only seed empty fields, so a retry after an error
    // keeps whatever the shopper already typed.
    /** @type {Array<[string, string|undefined]>} */
    var seeds = [
      ['first_name', prefill && prefill.first_name],
      ['last_name', prefill && prefill.last_name],
      ['email', prefill && prefill.email],
    ];
    seeds.forEach(function (seed) {
      var input = field(seed[0]);
      if (input && !input.value && seed[1]) input.value = seed[1];
    });
    showError('details', '');
    showStep('details');
    if (!needName) {
      var title = popup.querySelector('[data-title]');
      var subtitle = popup.querySelector('[data-subtitle]');
      if (title) title.textContent = EMAIL_ONLY_COPY.title;
      if (subtitle) subtitle.textContent = EMAIL_ONLY_COPY.subtitle;
    }
  }

  function openPopup() {
    manualOpen = true;
    pendingId = null;
    showError('phone', '');
    showError('otp', '');
    showStep('phone');
    popup.hidden = false;
    // header-actions.liquid's own mobile dropdown-close listener is a
    // bubble-phase document listener, same node our capture-phase
    // stopImmediatePropagation() above silences — it never gets a chance to
    // run, so a dropdown opened before Login was tapped is left open behind
    // this popup. We know we suppressed it, so close it ourselves.
    document.querySelectorAll('.account-dropdown.is-open').forEach(function (dropdown) {
      dropdown.classList.remove('is-open');
    });
    // Same [scroll-lock] attribute Horizon's own dialog.js/details disclosures
    // use (base.css: html[scroll-lock] { overflow: hidden }) — this popup
    // isn't a native <dialog>, so nothing locks the background for it otherwise.
    document.documentElement.setAttribute('scroll-lock', '');
    var contact = field('contact');
    if (contact) contact.focus();
  }

  function closePopup() {
    popup.hidden = true;
    document.documentElement.removeAttribute('scroll-lock');
    clearInterval(resendInterval);
    manualOpen = false;
  }

  function currentReturnTo() {
    return window.location.pathname + window.location.search;
  }

  // ── OTP step ─────────────────────────────────────────────────────────────

  function startResendCountdown() {
    var timer = popup.querySelector('[data-resend-timer]');
    var resend = actionButton('resend-otp');
    var left = RESEND_SECONDS;
    if (resend) resend.hidden = true;
    if (timer) timer.textContent = 'Resend OTP in ' + left + 's';
    clearInterval(resendInterval);
    resendInterval = window.setInterval(function () {
      left -= 1;
      if (left > 0) {
        if (timer) timer.textContent = 'Resend OTP in ' + left + 's';
        return;
      }
      clearInterval(resendInterval);
      if (timer) timer.textContent = "Didn't get it? ";
      if (resend) resend.hidden = false;
    }, 1000);
  }

  function refreshVerifyState() {
    var otpField = field('otp');
    var verify = actionButton('verify-otp');
    if (!verify || verify.classList.contains('is-loading')) return;
    verify.disabled = !(otpField && otpField.value.length === OTP_LENGTH && consentGiven());
  }

  // Same as the backend page: submits as soon as both the OTP and the tick
  // are in, whichever was completed last.
  function maybeAutoVerify() {
    refreshVerifyState();
    var verify = actionButton('verify-otp');
    if (verify && !verify.disabled) verifyOtp();
  }

  function sendOtp() {
    var contact = field('contact');
    var value = digits(contact ? contact.value : '').slice(-10);
    showError('phone', '');
    if (!/^\d{10}$/.test(value)) {
      showError('phone', 'Enter a valid 10-digit mobile number.');
      if (contact) contact.focus();
      return;
    }
    contactDigits = value;
    silent = null;
    var sendBtn = actionButton('send-otp');
    setLoading(sendBtn, true);
    var err = sso.initAuth({ contact: '+91' + value });
    if (err) {
      // Synchronous rejection (currently only otp_cooldown) — not a login
      // failure, just show the wait message and stay on this step.
      showError('phone', err.description);
      setLoading(sendBtn, false);
    }
    // null → in flight; login.attempt or login.failure takes it from here.
  }

  function resendOtp() {
    if (!contactDigits) return;
    showError('otp', '');
    var err = sso.initAuth({ contact: '+91' + contactDigits });
    if (err) showError('otp', err.description);
    else startResendCountdown();
  }

  function verifyOtp() {
    var otpField = field('otp');
    var otp = otpField ? otpField.value : '';
    if (otp.length !== OTP_LENGTH || !consentGiven()) return;
    showError('otp', '');
    silent = null;
    setLoading(actionButton('verify-otp'), true);
    sso.verifyAuth({ otp: otp });
    // → login.success or login.failure
  }

  // ── Backend calls ────────────────────────────────────────────────────────

  /**
   * @param {string} endpoint
   * @param {Record<string, string|boolean>} body
   * @returns {Promise<any>}
   */
  function postJson(endpoint, body) {
    return fetch(endpoint, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (r) {
        return r.json();
      })
      .catch(function () {
        return { status: false, message: 'Network error. Please try again.' };
      });
  }

  /**
   * MoEngage funnel tracking for the manual login flow — every stage from
   * OTP sent through to a required details step or any failure, so a drop-off
   * can be diagnosed from MoEngage alone. The phone number is sent as-is
   * (unmasked): it's the same identifier MoEngage already uses to identify
   * the profile, not a new exposure. Only covers the manual flow a shopper
   * actually asked for — auto/capture stay silent by design (see the header).
   * @param {string} name
   * @param {Record<string, any>} [props]
   */
  function trackLogin(name, props) {
    if (!w.AtombergMoe) return;
    var withPhone = Object.assign({}, props);
    if (contactDigits) withPhone.phone = '+91' + contactDigits;
    w.AtombergMoe.track(name, withPhone);
  }

  /**
   * Shared handler for every headless/* login response — a ready outcome
   * gets a real top-level navigation (the one hop that lets the backend's
   * Set-Cookie survive into Shopify's SSO redirect), everything else stays
   * inside this popup. The silent auto flow only ever follows a redirect: it
   * has no popup open to show a follow-up step or an error in.
   * @param {any} data
   * @param {string} errorStep
   * @param {boolean} manual
   */
  function handleOutcome(data, errorStep, manual) {
    if (data && data.status && data.redirect_url) {
      window.location.href = data.redirect_url;
      return;
    }
    if (!manual) return;
    if (data && data.status && data.outcome === 'needs_profile') {
      pendingId = data.pending_id;
      trackLogin('razorpay_details_required', {
        is_new_customer: true,
        needs_name: true,
        needs_email: true,
      });
      showDetails(true, true, true, data.prefill);
      return;
    }
    if (data && data.status && data.outcome === 'needs_details') {
      pendingId = data.pending_id;
      trackLogin('razorpay_details_required', {
        is_new_customer: false,
        needs_name: !!data.needs_name,
        needs_email: !!data.needs_email,
      });
      showDetails(false, !!data.needs_name, !!data.needs_email, data.prefill);
      return;
    }
    // The details step is already showing with the right groups and heading;
    // re-showing it would reset its copy.
    if (errorStep !== 'details') showStep(errorStep);
    if (data && data.email_taken) {
      // Someone else's email — it doesn't belong back in the box.
      var emailInput = field('email');
      if (emailInput) emailInput.value = '';
    }
    var errorMessage = (data && data.message) || 'Something went wrong. Please try again.';
    trackLogin('razorpay_login_failed', { step: 'backend', reason: errorMessage });
    showError(errorStep, errorMessage);
    setLoading(actionButton('verify-otp'), false);
    refreshVerifyState();
  }

  /**
   * @param {string} customerAuthCode
   * @param {boolean} manual
   */
  function resolveLogin(customerAuthCode, manual) {
    if (manual) showStep('loading');
    /** @type {Record<string, string|boolean>} */
    var body = { customer_auth_code: customerAuthCode, return_to: currentReturnTo() };
    if (manual) body.consent = consentGiven();
    postJson(resolveEndpoint, body).then(function (data) {
      if (!manual && data && data.outcome === 'captured') {
        // The cookie promised a silent login; the backend just said that's
        // impossible for this identity (record gone, or no email). Drop it
        // so the next page load goes through the capture gate instead —
        // which, with this suffix now recorded as a success, makes no call
        // at all. A later manual login sets the cookie again.
        writeCookie(AUTO_LOGIN_COOKIE, '', 0);
        applyCaptureResult(data);
        return;
      }
      handleOutcome(data, 'otp', manual);
    });
  }

  /**
   * Shared by the capture flow and the auto flow's capture fallback — both
   * answer with the same {mobile, consent_status} shape.
   * @param {any} data
   */
  function applyCaptureResult(data) {
    // A definitive answer from the backend either way (existing or newly
    // created) is 'success' — only a network/backend failure gets
    // 'failed', so the next visit retries instead of silently giving up.
    writeCaptureState(
      pendingCaptureSuffix,
      (data && data.mobile) || '',
      data && data.status ? 'success' : 'failed'
    );
    if (!data || !data.status || !data.mobile || !w.AtombergMoe) return;
    w.AtombergMoe.setUser({ id: data.mobile, mobile: data.mobile });
    w.AtombergMoe.setAttributes({
      consent_status: data.consent_status || '',
      identified_via: 'razorpay_silent',
    });
  }

  /** @param {string} customerAuthCode */
  function captureCustomer(customerAuthCode) {
    postJson(captureEndpoint, { customer_auth_code: customerAuthCode }).then(applyCaptureResult);
  }

  // ── SDK setup ────────────────────────────────────────────────────────────

  function initWidget() {
    if (w._atombergRazorpayIdWidgetInit || typeof w.RazorpayId !== 'function') return;
    w._atombergRazorpayIdWidgetInit = true;

    var instance = new w.RazorpayId({ key: key, platform: 'native', mode: 'headless' });
    if (!instance || instance.code) return; // SDK init error — trigger keeps its normal href.

    sso = instance;
    ready = true;
    // Read by atomberg-login-return-to.js, which also intercepts this anchor
    // and navigates programmatically — it yields to the popup when this is set.
    w._atombergRazorpayIdReady = true;

    sso.on(
      'init',
      function (
        /** @type {{customer?: {session_active: boolean, contact_suffix?: string}}} */ data
      ) {
        if (isLoggedIn || silent || manualOpen) return;
        if (!(data && data.customer && data.customer.session_active)) return;
        var suffix = data.customer.contact_suffix || '';
        // Both silent flows can end in a capture (auto falls back to one when
        // the login can't complete), so the suffix is kept for either.
        pendingCaptureSuffix = suffix;
        if (autoLoginAllowed) {
          silent = 'auto';
        } else if (!justLoggedOut && shouldCapture(suffix)) {
          silent = 'capture';
        } else {
          return;
        }
        sso.oneClickLogin();
      }
    );

    sso.on('login.attempt', function () {
      if (!manualOpen) return;
      trackLogin('razorpay_otp_sent');
      var sentTo = popup.querySelector('[data-sent-to]');
      if (sentTo) sentTo.textContent = '+91 ' + contactDigits;
      var otpField = field('otp');
      if (otpField) otpField.value = '';
      setLoading(actionButton('send-otp'), false);
      setLoading(actionButton('verify-otp'), false);
      showStep('otp');
      startResendCountdown();
      refreshVerifyState();
      if (otpField) otpField.focus();
    });

    sso.on('login.success', function (/** @type {{customer_auth_code?: string}} */ data) {
      var code = data && data.customer_auth_code;
      var silentFlow = silent;
      silent = null;
      if (!code) return;
      if (silentFlow === 'capture') captureCustomer(code);
      else if (silentFlow === 'auto') resolveLogin(code, false);
      else if (manualOpen) resolveLogin(code, true);
    });

    sso.on('login.failure', function (/** @type {{error: {description: string}}} */ data) {
      if (silent) {
        silent = null; // Silent flows fail silently — the shopper never asked for them.
        return;
      }
      if (!manualOpen) return;
      var otpStep = popup.querySelector('[data-step="otp"]');
      var onOtp = !!otpStep && !(/** @type {HTMLElement} */ (otpStep).hidden);
      var message =
        (data && data.error && data.error.description) || 'Something went wrong. Please try again.';
      if (!onOtp && fallbackLoginUrl) {
        // Razorpay's own OTP delivery failed before ever reaching the OTP
        // step — an outage on their side, not something retrying fixes, so
        // hand off to our own SMS-based OTP login instead of leaving the
        // shopper stuck. A verify failure (wrong OTP, onOtp true) is a typo,
        // not an outage — that stays inline so retyping isn't lost.
        trackLogin('razorpay_login_failed', {
          step: 'otp_send',
          reason: message,
          fallback_triggered: true,
        });
        window.location.href = fallbackLoginUrl;
        return;
      }
      trackLogin('razorpay_login_failed', {
        step: onOtp ? 'otp_verify' : 'otp_send',
        reason: message,
        fallback_triggered: false,
      });
      showError(onOtp ? 'otp' : 'phone', message);
      setLoading(actionButton('send-otp'), false);
      setLoading(actionButton('verify-otp'), false);
      refreshVerifyState();
    });
  }

  // The SDK is fetched by us (rather than a static <script async onload=...>
  // tag) so initWidget is guaranteed to exist before the SDK can ever call it
  // — an inline onload attribute would race an async script against this
  // file's own defer execution order.
  var sdkScript = document.createElement('script');
  sdkScript.src = SDK_SRC;
  sdkScript.async = true;
  sdkScript.onload = initWidget;
  document.head.appendChild(sdkScript);

  // ── Trigger click (capture phase) ───────────────────────────────────────
  // Capture phase: Horizon can re-render the header via section morphing, and
  // a listener bound to the anchor itself would be lost on that re-render —
  // same class of issue atomberg-checkout-guard.liquid documents. A delegated
  // document-level capture listener survives any DOM replacement.
  document.addEventListener(
    'click',
    function (event) {
      var target = /** @type {Element} */ (event.target);
      var trigger = target.closest(triggerSelector);
      if (!trigger) return;
      if (isLoggedIn) return; // Already signed in — never reopen the login popup.
      if (!ready) return; // Not ready yet — let the normal Login href proceed rather than dead-end the click.

      event.preventDefault();
      event.stopPropagation();
      // atomberg-login-return-to.js is a second document-level capture
      // listener on this same click — only stopImmediatePropagation() stops
      // another listener on the same node in the same phase.
      event.stopImmediatePropagation();
      openPopup();
    },
    true
  );

  // ── Popup-internal actions ───────────────────────────────────────────────

  var closeBtn = actionButton('close');
  if (closeBtn) closeBtn.addEventListener('click', closePopup);
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && !popup.hidden) closePopup();
  });

  var contactField = field('contact');
  if (contactField) {
    var contactInput = contactField;
    contactInput.addEventListener('input', function () {
      contactInput.value = digits(contactInput.value).slice(0, 10);
    });
    contactInput.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        sendOtp();
      }
    });
  }

  var sendOtpBtn = actionButton('send-otp');
  if (sendOtpBtn) sendOtpBtn.addEventListener('click', sendOtp);

  var editBtn = actionButton('edit-number');
  if (editBtn) {
    editBtn.addEventListener('click', function () {
      clearInterval(resendInterval);
      showStep('phone');
      var contact = field('contact');
      if (contact) contact.focus();
    });
  }

  var resendBtn = actionButton('resend-otp');
  if (resendBtn) resendBtn.addEventListener('click', resendOtp);

  var otpInputField = field('otp');
  if (otpInputField) {
    var otpInput = otpInputField;
    otpInput.addEventListener('input', function () {
      otpInput.value = digits(otpInput.value).slice(0, OTP_LENGTH);
      showError('otp', '');
      maybeAutoVerify();
    });
  }

  var consentBox = field('consent');
  if (consentBox) consentBox.addEventListener('change', maybeAutoVerify);

  var verifyBtn = actionButton('verify-otp');
  if (verifyBtn) verifyBtn.addEventListener('click', verifyOtp);

  var submitDetailsBtn = actionButton('submit-details');
  if (submitDetailsBtn) {
    var detailsBtn = submitDetailsBtn;
    detailsBtn.addEventListener('click', function () {
      showError('details', '');
      var firstNameField = field('first_name');
      var lastNameField = field('last_name');
      var emailField = field('email');
      var firstName = firstNameField ? firstNameField.value.trim() : '';
      var lastName = lastNameField ? lastNameField.value.trim() : '';
      var email = emailField ? emailField.value.trim() : '';

      if (details.name && (!firstName || !lastName)) {
        showError('details', 'Enter your first and last name to continue.');
        return;
      }
      if (details.email && !validEmail(email)) {
        showError('details', 'Enter a valid email address to continue.');
        return;
      }
      if (!pendingId) {
        showError('details', 'This sign-in attempt has expired. Please try again.');
        return;
      }

      /** @type {Record<string, string|boolean>} */
      var body = {
        pending_id: pendingId,
        return_to: currentReturnTo(),
        consent: consentGiven(),
      };
      if (details.name) {
        body.first_name = firstName;
        body.last_name = lastName;
      }
      if (details.email) body.email = email;

      setLoading(detailsBtn, true);
      postJson(details.create ? profileEndpoint : detailsEndpoint, body).then(function (data) {
        setLoading(detailsBtn, false);
        handleOutcome(data, 'details', true);
      });
    });
  }
})();
